import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Alert,
  Link as MuiLink,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Snackbar,
  Button,
  FormControlLabel,
  Switch,
} from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import MovieIcon from "@mui/icons-material/Movie";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import { videoService } from "../services/api";
import VimeoModal from "./VimeoModal";

// Función helper para extraer la marca del nombre de archivo
const extractMarca = (fileName) => {
  if (!fileName || typeof fileName !== "string") return "";
  const parts = fileName.replace(".mp4", "").split("_");
  // Devuelve todo desde la segunda '_' en adelante, o un string indicativo si no hay suficientes partes
  return parts.length >= 3 ? parts.slice(2).join("_") : "Nombre Inesperado";
};

const normalizeVideoStructure = (videosData) => {
  if (!Array.isArray(videosData)) return [];

  const sortedSalas = videosData.sort((a, b) =>
    a.nombre.localeCompare(b.nombre, undefined, { numeric: true })
  );

  sortedSalas.forEach((sala) => {
    if (sala.fechas && Array.isArray(sala.fechas)) {
      sala.fechas.forEach((fecha) => {
        if (fecha.videos && Array.isArray(fecha.videos)) {
          fecha.videos.forEach((video) => {
            video.salaId = sala.id;
            video.fechaId = fecha.id;
          });
        } else {
          fecha.videos = [];
        }
      });
    } else {
      sala.fechas = [];
    }
  });

  return sortedSalas;
};

const findVideoInStructure = (videosStructure, videoId) => {
  for (const sala of videosStructure || []) {
    for (const fecha of sala.fechas || []) {
      for (const video of fecha.videos || []) {
        if (video.id === videoId) {
          return video;
        }
      }
    }
  }
  return null;
};

const buildVimeoInfoFromSyncData = (syncData) => ({
  videoId: syncData.videoId,
  url: syncData.url,
  embedUrl: syncData.embedUrl,
});

// Este componente ahora maneja AMBAS vistas: pública y privada (dashboard)
const VideoList = ({ isPublic = false, canDelete = true }) => {
  // Prop para diferenciar vistas
  const [structure, setStructure] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  
  // Estados para Vimeo
  const [vimeoConfig, setVimeoConfig] = useState(null);
  const [vimeoModalOpen, setVimeoModalOpen] = useState(false);
  const [selectedVideoForVimeo, setSelectedVideoForVimeo] = useState(null);
  const [sendingToVimeo, setSendingToVimeo] = useState(false);
  const [deletingFromVimeo, setDeletingFromVimeo] = useState(false);

  // Estados para diálogos y feedback
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteFromVimeo, setDeleteFromVimeo] = useState(true); // Switch para eliminar de Vimeo
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null); // { id, nombre, salaId, fechaId }
  const [newMarca, setNewMarca] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Cargar configuración de Vimeo (solo si no es público)
  useEffect(() => {
    if (!isPublic) {
      loadVimeoConfig();
    }
  }, [isPublic]);

  const loadVimeoConfig = async () => {
    try {
      const response = await videoService.getVimeoConfig();
      if (response.success) {
        setVimeoConfig(response.data);
      }
    } catch (err) {
      console.warn('Error cargando configuración de Vimeo:', err);
    }
  };

  const fetchVideos = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
      setError("");
    }

    try {
      const response = await videoService.getVideos();
      if (response.success && Array.isArray(response.data)) {
        const normalized = normalizeVideoStructure(response.data);
        setStructure(normalized);
        return normalized;
      }

      if (!silent) {
        setError(
          response.message ||
            "Error: La respuesta del servidor no tiene el formato esperado."
        );
        setStructure([]);
      }
      return null;
    } catch (err) {
      if (!silent) {
        setError(err.message || "Error de conexión al cargar los videos.");
        setStructure([]);
      }
      return null;
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, []);

  // Fetch inicial
  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  // Refresco silencioso periódico para capturar cambios de subidas automáticas a Vimeo
  useEffect(() => {
    if (isPublic) return undefined;

    const intervalId = setInterval(() => {
      fetchVideos({ silent: true });
    }, 15000);

    return () => clearInterval(intervalId);
  }, [isPublic, fetchVideos]);

  // Si el modal está abierto y la subida automática aún no refleja URL,
  // hacer polling para actualizar el modal en vivo sin exigir reabrir.
  useEffect(() => {
    if (
      isPublic ||
      !vimeoModalOpen ||
      !selectedVideoForVimeo?.id ||
      !vimeoConfig?.enabled ||
      !vimeoConfig?.autoUpload ||
      selectedVideoForVimeo?.vimeo?.videoId
    ) {
      return undefined;
    }

    let attempts = 0;
    const maxAttempts = 24; // ~2 minutos

    const intervalId = setInterval(async () => {
      attempts += 1;

      // Intentar reconciliar metadata faltante (casos donde Vimeo subió,
      // pero Drive no guardó propiedades por error transitorio).
      try {
        const syncResponse = await videoService.syncVimeoStatus(
          selectedVideoForVimeo.id
        );

        if (syncResponse?.success && syncResponse?.data?.videoId) {
          const vimeoInfo = buildVimeoInfoFromSyncData(syncResponse.data);

          setStructure((prevStructure) =>
            prevStructure.map((sala) => ({
              ...sala,
              fechas: sala.fechas.map((fecha) => ({
                ...fecha,
                videos: fecha.videos.map((v) =>
                  v.id === selectedVideoForVimeo.id
                    ? { ...v, vimeo: vimeoInfo }
                    : v
                ),
              })),
            }))
          );

          setSelectedVideoForVimeo((prev) =>
            prev && prev.id === selectedVideoForVimeo.id
              ? { ...prev, vimeo: vimeoInfo }
              : prev
          );

          clearInterval(intervalId);
          return;
        }
      } catch (syncErr) {
        // Ignorar en polling; continuamos con fetch normal.
      }

      const latestStructure = await fetchVideos({ silent: true });
      if (!latestStructure) {
        if (attempts >= maxAttempts) {
          clearInterval(intervalId);
        }
        return;
      }

      const refreshedVideo = findVideoInStructure(
        latestStructure,
        selectedVideoForVimeo.id
      );

      if (refreshedVideo) {
        setSelectedVideoForVimeo(refreshedVideo);
        if (refreshedVideo?.vimeo?.videoId) {
          clearInterval(intervalId);
          return;
        }
      }

      if (attempts >= maxAttempts) {
        clearInterval(intervalId);
      }
    }, 5000);

    return () => clearInterval(intervalId);
  }, [
    isPublic,
    vimeoModalOpen,
    selectedVideoForVimeo,
    vimeoConfig,
    fetchVideos,
  ]);

  // --- Handlers para acciones ---

  const handleDeleteClick = (video) => {
    setCurrentItem(video);
    setDeleteFromVimeo(true); // Resetear a true (activado por defecto)
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!currentItem) return;
    setActionLoading(true);
    try {
      // Si tiene info de Vimeo y el switch está activado, eliminar primero de Vimeo
      if (deleteFromVimeo && currentItem.vimeo && currentItem.vimeo.videoId) {
        try {
          const vimeoResponse = await videoService.deleteFromVimeo(currentItem.id);
          if (!vimeoResponse.success) {
            console.warn('Error al eliminar de Vimeo:', vimeoResponse.message);
            // Continuar con la eliminación de Drive aunque falle Vimeo
          }
        } catch (vimeoErr) {
          console.warn('Error al eliminar de Vimeo:', vimeoErr);
          // Continuar con la eliminación de Drive aunque falle Vimeo
        }
      }
      
      // Eliminar de Google Drive
      const response = await videoService.deleteVideo(currentItem.id);
      if (response.success) {
        setSnackbar({
          open: true,
          message: deleteFromVimeo && currentItem.vimeo?.videoId 
            ? "Video eliminado correctamente de Drive y Vimeo" 
            : "Video eliminado correctamente de Drive",
          severity: "success",
        });
        // Actualización local del estado (más robusta)
        setStructure((prevStructure) => {
          const newStructure = prevStructure
            .map((sala) => {
              if (sala.id !== currentItem.salaId) return sala;
              const newFechas = sala.fechas
                .map((fecha) => {
                  if (fecha.id !== currentItem.fechaId) return fecha;
                  const newVideos = fecha.videos.filter(
                    (v) => v.id !== currentItem.id
                  );
                  return { ...fecha, videos: newVideos };
                })
                .filter((fecha) => fecha.videos.length > 0); // Quitar fechas si quedan vacías
              return { ...sala, fechas: newFechas };
            })
            .filter((sala) => sala.fechas.length > 0); // Quitar salas si quedan vacías
          return newStructure;
        });
      } else {
        throw new Error(
          response.message || "Error al eliminar el video desde el servidor."
        );
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || "Error de conexión al eliminar",
        severity: "error",
      });
    } finally {
      setActionLoading(false);
      setDeleteConfirmOpen(false);
      setCurrentItem(null);
    }
  };

  const handleEditClick = (video) => {
    setCurrentItem(video);
    setNewMarca(extractMarca(video.nombre));
    setEditModalOpen(true);
  };

  const handleEditSave = async () => {
    if (!currentItem || !newMarca.trim()) {
      setSnackbar({
        open: true,
        message: "El nombre de la marca no puede estar vacío.",
        severity: "warning",
      });
      return;
    }
    setActionLoading(true);
    try {
      const response = await videoService.renameVideo(
        currentItem.id,
        newMarca.trim()
      );
      if (response.success) {
        setSnackbar({
          open: true,
          message: "Marca actualizada correctamente",
          severity: "success",
        });
        // Actualización local del estado
        setStructure((prevStructure) =>
          prevStructure.map((sala) =>
            sala.id === currentItem.salaId
              ? {
                  ...sala,
                  fechas: sala.fechas.map((fecha) =>
                    fecha.id === currentItem.fechaId
                      ? {
                          ...fecha,
                          videos: fecha.videos.map((v) =>
                            v.id === currentItem.id
                              ? { ...v, nombre: response.newName }
                              : v
                          ),
                        }
                      : fecha
                  ),
                }
              : sala
          )
        );
      } else {
        throw new Error(
          response.message || "Error al actualizar la marca desde el servidor."
        );
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || "Error de conexión al actualizar",
        severity: "error",
      });
    } finally {
      setActionLoading(false);
      setEditModalOpen(false);
      setCurrentItem(null);
      setNewMarca("");
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Abrir modal de Vimeo
  const handleVimeoClick = async (video) => {
    let selectedVideo = video;

    // Si la subida automática está activada y no hay info de Vimeo,
    // refrescar antes de abrir para evitar falsos "falló" por datos stale.
    if (
      !isPublic &&
      vimeoConfig?.enabled &&
      vimeoConfig?.autoUpload &&
      !video?.vimeo?.videoId
    ) {
      // 1) Intentar reconciliación directa en backend
      try {
        const syncResponse = await videoService.syncVimeoStatus(video.id);
        if (syncResponse?.success && syncResponse?.data?.videoId) {
          const vimeoInfo = buildVimeoInfoFromSyncData(syncResponse.data);

          setStructure((prevStructure) =>
            prevStructure.map((sala) => ({
              ...sala,
              fechas: sala.fechas.map((fecha) => ({
                ...fecha,
                videos: fecha.videos.map((v) =>
                  v.id === video.id ? { ...v, vimeo: vimeoInfo } : v
                ),
              })),
            }))
          );

          selectedVideo = {
            ...video,
            vimeo: vimeoInfo,
          };
        }
      } catch (syncErr) {
        // Ignorar; seguimos con refresco clásico.
      }

      // 2) Refrescar estado local desde /api/videos para evitar datos stale.
      const latestStructure = await fetchVideos({ silent: true });
      if (latestStructure) {
        const refreshedVideo = findVideoInStructure(latestStructure, video.id);
        if (refreshedVideo) {
          selectedVideo = refreshedVideo;
        }
      }
    }

    setSelectedVideoForVimeo(selectedVideo);
    setVimeoModalOpen(true);
  };

  // Enviar video a Vimeo desde el modal
  const handleSendToVimeo = async (videoId, title, description) => {
    setSendingToVimeo(true);
    try {
      const response = await videoService.sendToVimeo(
        videoId,
        title,
        description,
        vimeoConfig?.privacy || 'unlisted'
      );

      if (response.success) {
        setSnackbar({
          open: true,
          message: '✅ Video enviado a Vimeo correctamente',
          severity: 'success',
        });
        
        // Actualizar el video en la estructura local con la info de Vimeo
        setStructure((prevStructure) =>
          prevStructure.map((sala) => ({
            ...sala,
            fechas: sala.fechas.map((fecha) => ({
              ...fecha,
              videos: fecha.videos.map((v) =>
                v.id === videoId
                  ? {
                      ...v,
                      vimeo: {
                        videoId: response.data.videoId,
                        url: response.data.url,
                        embedUrl: response.data.embedUrl,
                      },
                    }
                  : v
              ),
            })),
          }))
        );
        
        // Actualizar el video seleccionado para reflejar los cambios en el modal
        setSelectedVideoForVimeo((prev) => ({
          ...prev,
          vimeo: {
            videoId: response.data.videoId,
            url: response.data.url,
            embedUrl: response.data.embedUrl,
          },
        }));
      } else {
        throw new Error(response.message || 'Error enviando a Vimeo');
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || 'Error enviando video a Vimeo',
        severity: 'error',
      });
    } finally {
      setSendingToVimeo(false);
    }
  };

  // Eliminar video de Vimeo desde el modal
  const handleDeleteFromVimeo = async (videoId) => {
    setDeletingFromVimeo(true);
    try {
      const response = await videoService.deleteFromVimeo(videoId);

      if (response.success) {
        setSnackbar({
          open: true,
          message: '✅ Video eliminado de Vimeo correctamente',
          severity: 'success',
        });
        
        // Actualizar el video en la estructura local eliminando la info de Vimeo
        setStructure((prevStructure) =>
          prevStructure.map((sala) => ({
            ...sala,
            fechas: sala.fechas.map((fecha) => ({
              ...fecha,
              videos: fecha.videos.map((v) =>
                v.id === videoId
                  ? {
                      ...v,
                      vimeo: null,
                    }
                  : v
              ),
            })),
          }))
        );
        
        // Cerrar el modal
        setVimeoModalOpen(false);
        setSelectedVideoForVimeo(null);
      } else {
        throw new Error(response.message || 'Error eliminando de Vimeo');
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || 'Error eliminando video de Vimeo',
        severity: 'error',
      });
    } finally {
      setDeletingFromVimeo(false);
    }
  };

  // Determinar cuándo mostrar botón de Vimeo
  const showVimeoButton = (video) => {
    // Vista pública: solo si tiene info de Vimeo
    if (isPublic) {
      return video.vimeo && video.vimeo.videoId;
    }
    
    // Vista privada: siempre si Vimeo está habilitado y hay token
    if (vimeoConfig?.enabled && vimeoConfig?.hasToken) {
      // Tanto en modo manual como automático, mostrar siempre el botón
      // Esto permite:
      // - Modo manual: subir videos manualmente
      // - Modo automático: resubir videos si falló la subida automática o si fue eliminado solo de Vimeo
      return true;
    }
    
    return false;
  };

  // --- Renderizado ---

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "calc(100vh - 200px)",
          p: 3,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (structure.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        {/* Usar Typography en lugar de Alert para "No hay videos" */}
        <Typography variant="h6" align="center" color="text.secondary">
          No hay videos disponibles.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Typography
        variant={isPublic ? "h4" : "h5"}
        gutterBottom
        align="center"
        sx={{ mb: 3 }}
      >
        {isPublic ? "FTP de Videos" : "Gestionar Videos Subidos"}
      </Typography>

      {structure.map((sala) => (
        <Accordion
          key={sala.id}
          defaultExpanded={structure.length === 1 && isPublic}
          sx={{ mb: 1.5, bgcolor: "background.paper" }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: "text.primary" }} />}
            aria-controls={`sala-${sala.id}-content`}
            id={`sala-${sala.id}-header`}
          >
            <FolderIcon sx={{ mr: 1, color: "primary.main" }} />
            <Typography variant="h6">{sala.nombre}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            {!sala.fechas || sala.fechas.length === 0 ? (
              <Typography
                sx={{ p: 2, fontStyle: "italic", color: "text.secondary" }}
              >
                No hay videos en esta sala.
              </Typography>
            ) : (
              <List dense disablePadding>
                {sala.fechas.map((fecha) => (
                  <Accordion
                    key={fecha.id}
                    sx={{
                      boxShadow: "none",
                      "&:before": { display: "none" },
                      bgcolor: "inherit",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={
                        <ExpandMoreIcon sx={{ color: "text.secondary" }} />
                      }
                      aria-controls={`fecha-${fecha.id}-content`}
                      id={`fecha-${fecha.id}-header`}
                      sx={{ borderTop: `1px solid rgba(255, 255, 255, 0.12)` }}
                    >
                      <FolderIcon
                        sx={{
                          mr: 1,
                          fontSize: "1.1rem",
                          color: "text.secondary",
                        }}
                      />
                      <Typography variant="subtitle1">
                        {fecha.nombre}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{ pl: !isPublic ? 2 : 4, pr: 1, pt: 0, pb: 1 }}
                    >
                      {!fecha.videos || fecha.videos.length === 0 ? (
                        <Typography
                          sx={{ fontStyle: "italic", color: "text.secondary" }}
                        >
                          No hay videos para esta fecha.
                        </Typography>
                      ) : (
                        <List dense disablePadding>
                          {fecha.videos.map((video) => (
                            <ListItem
                              key={video.id}
                              secondaryAction={
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  flexShrink: 0,
                                }}
                              >
                                {/* Botón de Vimeo (se muestra según condiciones) */}
                                {showVimeoButton(video) && (
                                  <Tooltip title={
                                    isPublic 
                                      ? "Ver en Vimeo" 
                                      : (video.vimeo?.videoId ? "Ver en Vimeo" : "Enviar a Vimeo")
                                  }>
                                    <IconButton
                                      edge="end"
                                      aria-label="vimeo"
                                      sx={{ mr: 0.5 }}
                                      onClick={() => handleVimeoClick(video)}
                                      disabled={actionLoading}
                                    >
                                      <VideoLibraryIcon 
                                        fontSize="small" 
                                        sx={{ 
                                          color: video.vimeo?.videoId ? "success.main" : "info.main" 
                                        }} 
                                      />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                
                                {!isPublic && (
                                  <>
                                    <Tooltip title="Editar Marca">
                                      <IconButton
                                        edge="end"
                                        aria-label="editar"
                                        sx={{ mr: 0.5 }}
                                        onClick={() => handleEditClick(video)}
                                        disabled={actionLoading}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    {canDelete && (
                                      <Tooltip title="Eliminar Video">
                                        <IconButton
                                          edge="end"
                                          aria-label="eliminar"
                                          sx={{ mr: 0.5 }}
                                          onClick={() =>
                                            handleDeleteClick(video)
                                          }
                                          disabled={actionLoading}
                                        >
                                          <DeleteIcon
                                            fontSize="small"
                                            sx={{ color: "error.light" }}
                                          />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </>
                                )}
                                <Tooltip title="Descargar Video">
                                  <IconButton
                                    edge="end"
                                    aria-label="descargar"
                                    component={MuiLink}
                                    href={video.enlaceDescarga}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    disabled={actionLoading}
                                  >
                                    <DownloadIcon
                                      sx={{ color: "primary.main" }}
                                    />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                              }
                              sx={{
                                borderBottom: `1px solid rgba(255, 255, 255, 0.05)`,
                                pl: !isPublic ? 2 : 0, // Ajustar padding izquierdo
                                pr: 1, // Asegurar padding derecho
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: "auto", mr: 1.5 }}>
                                <MovieIcon sx={{ color: "text.secondary" }} />
                              </ListItemIcon>
                              <ListItemText
                                primary={video.nombre}
                                primaryTypographyProps={{
                                  style: {
                                    // whiteSpace: 'nowrap', // Quitar nowrap para permitir wrap
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    // Añadir un maxWidth implícito restando espacio a los botones
                                    // Esto es aproximado, puede necesitar ajuste fino
                                    maxWidth: isPublic
                                      ? "calc(100% - 40px)"
                                      : "calc(100% - 120px)",
                                  },
                                }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </List>
            )}
          </AccordionDetails>
        </Accordion>
      ))}

      {/* --- Diálogos y Snackbar --- */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            ¿Estás seguro de que deseas eliminar el video "{currentItem?.nombre}
            "? Esta acción no se puede deshacer.
          </DialogContentText>
          
          {/* Mostrar switch solo si el video tiene info de Vimeo */}
          {currentItem?.vimeo && currentItem.vimeo.videoId && (
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={deleteFromVimeo}
                    onChange={(e) => setDeleteFromVimeo(e.target.checked)}
                    disabled={actionLoading}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    {deleteFromVimeo 
                      ? "Eliminar también de Vimeo" 
                      : "Eliminar solo de Drive (mantener en Vimeo)"}
                  </Typography>
                }
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            disabled={actionLoading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            autoFocus
            color="error"
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={20} /> : "Eliminar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Editar Marca del Video</DialogTitle>
        <DialogContent>
          <Typography variant="caption" display="block" gutterBottom>
            Video: {currentItem?.nombre}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            id="marca"
            label="Nueva Marca"
            type="text"
            fullWidth
            variant="standard"
            value={newMarca}
            onChange={(e) => setNewMarca(e.target.value)}
            disabled={actionLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setEditModalOpen(false)}
            disabled={actionLoading}
          >
            Cancelar
          </Button>
          <Button onClick={handleEditSave} disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={20} /> : "Guardar Cambios"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Vimeo */}
      <VimeoModal
        open={vimeoModalOpen}
        onClose={() => {
          setVimeoModalOpen(false);
          setSelectedVideoForVimeo(null);
        }}
        video={selectedVideoForVimeo}
        isPublic={isPublic}
        isManualMode={!vimeoConfig?.autoUpload}
        onSendToVimeo={handleSendToVimeo}
        onDeleteFromVimeo={handleDeleteFromVimeo}
        sending={sendingToVimeo}
        deleting={deletingFromVimeo}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VideoList;
